import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import AxiosInstance from '../../../helpers/AxiosInstance';

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    try {
      // Récupérer l'ID de commande depuis l'URL ou le localStorage
      const urlParams = new URLSearchParams(location.search);
      const orderId = urlParams.get('orderId') || localStorage.getItem('lastOrderId');
      const errorParam = urlParams.get('error');
      
      // Check for error parameters first
      if (errorParam) {
        if (errorParam === 'payment_failed') {
          setError('Le paiement a échoué. Votre carte a été refusée ou le paiement a été annulé.');
        } else if (errorParam === 'callback_error') {
          setError('Erreur lors du traitement du paiement. Veuillez réessayer.');
        } else {
          setError('Une erreur est survenue lors du paiement.');
        }
        setLoading(false);
        return;
      }
      
      if (!orderId) {
        setError('ID de commande non trouvé');
        setLoading(false);
        return;
      }

      // Store orderId for use in download links
      window.orderId = orderId;

      const response = await AxiosInstance.get(`/visitor/payment/status/${orderId}`);
      console.log('Payment status response:', response);
      console.log('Order items:', response?.order?.items);
      setPaymentStatus(response);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Erreur lors de la vérification du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3">Vérification du paiement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
            <button
              className="btn btn-primary"
              onClick={handleGoHome}
            >
              <i className="fas fa-home me-2"></i>
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isPaymentSuccessful = paymentStatus?.isPaid === true;

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-body text-center">
              {isPaymentSuccessful ? (
                <>
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                  <h2 className="text-success mt-3">Paiement Réussi !</h2>
                  <p className="lead">Votre commande a été traitée avec succès</p>
                  <p className="text-muted">Un reçu détaillé a été envoyé à votre adresse email.</p>
                  
                  {paymentStatus?.payment && (
                    <div className="mt-4 p-3 bg-light rounded">
                      <h5>Détails de la Commande</h5>
                      <p><strong>Numéro de commande:</strong> {paymentStatus.payment.orderNumber}</p>
                      <p><strong>Montant:</strong> {paymentStatus.payment.amount} DA</p>
                      <p><strong>Date:</strong> {new Date(paymentStatus.payment.createdAt).toLocaleString('fr-FR')}</p>
                    </div>
                  )}

                  {/* Purchased Books Section */}
                  {paymentStatus?.payment?.order?.items && paymentStatus.payment.order.items.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-start mb-3">
                        <i className="fas fa-book me-2"></i>
                        Vos Livres Achetés ({paymentStatus.payment.order.items.length})
                      </h5>
                      <div className="row">
                        {paymentStatus.payment.order.items.map((item, index) => (
                          <div key={item.id || index} className="col-md-6 mb-3">
                            <div className="card h-100">
                              <div className="card-body">
                                <h6 className="card-title">{item.bookTitle}</h6>
                                <p className="card-text text-muted small">
                                  Prix: {item.unitPrice} DA
                                </p>
                                <div className="d-grid gap-2">
                                  <a 
                                    href={`/api/pdf/visitor/${item.bookId}?orderId=${window.orderId || ''}`}
                                    className="btn btn-primary btn-sm"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <i className="fas fa-download me-2"></i>
                                    Télécharger PDF
                                  </a>
                                  <p className="text-muted small mt-2">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Cliquez sur "Télécharger PDF" pour accéder au livre complet
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <i className="fas fa-clock text-warning" style={{ fontSize: '4rem' }}></i>
                  <h2 className="text-warning mt-3">Paiement en Cours</h2>
                  <p className="lead">Votre paiement est en cours de traitement</p>
                  <p className="text-muted">Vous recevrez un email de confirmation une fois le paiement confirmé.</p>
                  
                  {paymentStatus?.statusMessage && (
                    <div className="alert alert-info mt-3">
                      {paymentStatus.statusMessage}
                    </div>
                  )}
                </>
              )}

              <div className="mt-4">
                <button
                  className="btn btn-outline-primary me-2"
                  onClick={() => window.print()}
                >
                  <i className="fas fa-print me-2"></i>
                  Imprimer le Reçu
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleGoHome}
                >
                  <i className="fas fa-home me-2"></i>
                  Retour à l'Accueil
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;